import React from 'react';
import { Check, X } from 'lucide-react';

// Password policy: minimum 12 characters with upper/lower case, a digit and a special character.
export const PASSWORD_MIN_LENGTH = 12;

export const getPasswordChecks = (password = '') => ({
  length: password.length >= PASSWORD_MIN_LENGTH,
  uppercase: /[A-Z]/.test(password),
  lowercase: /[a-z]/.test(password),
  digit: /[0-9]/.test(password),
  special: /[^A-Za-z0-9]/.test(password),
});

export const passwordMeetsPolicy = (password = '') =>
  Object.values(getPasswordChecks(password)).every(Boolean);

export const getPasswordPolicyError = (password = '') => {
  const checks = getPasswordChecks(password);
  if (!checks.length) return `Le mot de passe doit contenir au moins ${PASSWORD_MIN_LENGTH} caractères`;
  if (!checks.uppercase) return 'Le mot de passe doit contenir au moins une majuscule';
  if (!checks.lowercase) return 'Le mot de passe doit contenir au moins une minuscule';
  if (!checks.digit) return 'Le mot de passe doit contenir au moins un chiffre';
  if (!checks.special) return 'Le mot de passe doit contenir au moins un caractère spécial';
  return null;
};

const REQUIREMENTS = [
  { key: 'length', label: `Au moins ${PASSWORD_MIN_LENGTH} caractères` },
  { key: 'uppercase', label: 'Une majuscule' },
  { key: 'lowercase', label: 'Une minuscule' },
  { key: 'digit', label: 'Un chiffre' },
  { key: 'special', label: 'Un caractère spécial (!@#...)' },
];

const STRENGTH_LEVELS = [
  { label: 'Très faible', color: 'bg-red-500', text: 'text-red-600' },
  { label: 'Faible', color: 'bg-orange-500', text: 'text-orange-600' },
  { label: 'Moyen', color: 'bg-amber-500', text: 'text-amber-600' },
  { label: 'Bon', color: 'bg-lime-500', text: 'text-lime-600' },
  { label: 'Fort', color: 'bg-emerald-500', text: 'text-emerald-600' },
];

const PasswordStrengthMeter = ({ password = '', showRequirements = true }) => {
  if (!password) return null;

  const checks = getPasswordChecks(password);
  const satisfied = Object.values(checks).filter(Boolean).length;
  // Score from 0 to 4 (map 5 satisfied checks onto 5 levels, clamped)
  const level = Math.max(0, Math.min(satisfied - 1, STRENGTH_LEVELS.length - 1));
  const strength = STRENGTH_LEVELS[level];

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex gap-1">
          {STRENGTH_LEVELS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                i <= level ? strength.color : 'bg-slate-200 dark:bg-slate-700'
              }`}
            />
          ))}
        </div>
        <span className={`text-xs font-medium ${strength.text}`}>{strength.label}</span>
      </div>

      {/* Requirements checklist */}
      {showRequirements && (
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
          {REQUIREMENTS.map((req) => (
            <li
              key={req.key}
              className={`flex items-center gap-1.5 text-xs ${
                checks[req.key] ? 'text-emerald-600' : 'text-slate-500'
              }`}
            >
              {checks[req.key] ? (
                <Check className="w-3.5 h-3.5 flex-shrink-0" />
              ) : (
                <X className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />
              )}
              {req.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PasswordStrengthMeter;
